import { useState, useEffect, createContext, useContext } from 'react';
import posthog from 'posthog-js';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/accounts/profile/')
        .then(res => {
          setUser(res.data);
          posthog.identify(res.data.id, { email: res.data.email, company: res.data.company_name });
        })
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // The auth token lives in localStorage (shared across all tabs) but `user` is
  // per-tab React state. If a different account logs in (or out) in another tab,
  // the token changes underneath this tab and its requests would silently use the
  // other account's token, mixing data up (campaigns from the wrong account). React
  // to cross-tab token changes by reloading so this tab re-syncs to the new token.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'token' && e.oldValue !== e.newValue) {
        window.location.reload();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = async (email, password, cf_turnstile_token = '') => {
    const res = await api.post('/accounts/login/', { email, password, cf_turnstile_token });
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    posthog.identify(res.data.user.id, { email: res.data.user.email, company: res.data.user.company_name });
    posthog.capture('user_logged_in', { method: 'email' });
    return res.data;
  };

  const register = async (email, password, company_name, cf_turnstile_token = '') => {
    const res = await api.post('/accounts/register/', { email, password, company_name, cf_turnstile_token });
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    posthog.identify(res.data.user.id, { email: res.data.user.email, company: res.data.user.company_name });
    posthog.capture('user_registered', { method: 'email' });
    return res.data;
  };

  const forgotPassword = async (email) => {
    const res = await api.post('/accounts/password-reset/', { email });
    return res.data;
  };

  const resetPassword = async (uid, token, new_password) => {
    const res = await api.post('/accounts/password-reset-confirm/', { uid, token, new_password });
    return res.data;
  };

  const googleLogin = async (credential) => {
    const res = await api.post('/accounts/google/login/', { credential });
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    posthog.identify(res.data.user.id, { email: res.data.user.email, company: res.data.user.company_name });
    posthog.capture('user_logged_in', { method: 'google' });
    return res.data;
  };

  const refreshUser = async () => {
    try {
      const res = await api.get('/accounts/profile/');
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    posthog.reset();
  };

  return (
    <AuthContext.Provider value={{ user, login, register, googleLogin, logout, forgotPassword, resetPassword, refreshUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
